import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Container, Accordion } from './styles';
import { useNavigate } from 'react-router-dom';
import Api, { exceptionNotificationAPI } from '../../services/api';

import StatusCards from './StatusCards';
import FilterBar from './FilterBar';
import CompanyAccordion from './CompanyAccordion';
import TableLoading from '../TableLoading';
import SessionTimeout from '../SessionTimeout';

const STATUS_OPTIONS = [
    { value: 0, text: "Not Started" },
    { value: 1, text: "Saved as Draft" },
    { value: 2, text: "Sent to Local Approval" },
    { value: 3, text: "Reopened by Local Approver" },
    { value: 4, text: "Sent to Global Approval" },
    { value: 5, text: "Reopened by Global Approver" },
    { value: 6, text: "Approved" }
];

const VERSION_OPTIONS = [
    { value: 0, text: "Partial" },
    { value: 1, text: "Final" }
];

const ROLE_GLOBAL_APPROVER = "23D9D409-D7AA-4966-9047-48C04B41F0A1";
const ROLE_LOCAL_APPROVER = "198E176A-E8F5-437C-B6B9-AAB5972099F4";
const ROLE_SUPPLIER = "C1FF151F-F179-4D1F-B1B1-378B9E6D996F";
const ROLE_VIEWER = "E759AB01-E2C8-4D38-BA5F-BA30949FB70F";

function DashboardGlobal({ user }) {
    const navigate = useNavigate();
    const isFetchingRef = useRef(false);
    const lastFetchKey = useRef('');
    const companiesLoadedRef = useRef(false);
    
    const [userObject, setUserObject] = useState(null);
    const [companies, setCompanies] = useState([]);
    const [companiesDefault, setCompaniesDefault] = useState([]);
    const [searchSupplier, setSearchSupplier] = useState([]);
    const [crops, setCrops] = useState([]);
    const [operations, setOperations] = useState([]);
    const [modules, setModules] = useState([]);
    
    const [selectedCompanyIndex, setSelectedCompanyIndex] = useState([]);
    const [selectedOperation, setSelectedOperation] = useState("");
    const [selectedCrop, setSelectedCrop] = useState("");
    const [selectedModule, setSelectedModule] = useState("");
    const [selectedStatus, setSelectedStatus] = useState("");
    const [selectedVersion, setSelectedVersion] = useState("");
    const [selectedCulture, setSelectedCulture] = useState("");
    
    const [loadingFilteredData, setLoadingFilteredData] = useState(false);
    const [statusCard, setStatusCard] = useState(true);
    const [collapseAll, setCollapseAll] = useState(false);

    // Calcular percentagens dos cards de status
    /*const statusPercentages = useMemo(() => {
        if (selectedCompanyIndex.length === 0) {
            return { notStarted: 0, partial: 0, final: 0, completed: 0 };
        }

        let notStarted = 0;
        let partial = 0;
        let final = 0;
        let completed = 0;

        selectedCompanyIndex.forEach((ind) => {
            const company = searchSupplier[ind];
            if (company && company.operations) {
                company.operations.forEach((op) => {
                    notStarted += op.notStarted || 0;
                    partial += op.partial || 0;
                    final += op.final || 0;
                    completed += op.completed || 0;
                });
            }
        });

        const total = notStarted + partial + final + completed;
        if (total === 0) {
            return { notStarted: 0, partial: 0, final: 0, completed: 0 };
        }

        return {
            notStarted: (notStarted / total * 100).toFixed(2),
            partial: (partial / total * 100).toFixed(2),
            final: (final / total * 100).toFixed(2),
            completed: (completed / total * 100).toFixed(2)
        };
    }, [searchSupplier, selectedCompanyIndex]);*/

    // Filtrar módulos baseado na operação selecionada
    const modulesFiltered = useMemo(() => {
        if (selectedOperation) {
            return modules.filter(m => m.operationID === parseInt(selectedOperation));
        }
        return modules;
    }, [modules, selectedOperation]);

    // Filtrar opções de status disponíveis
    const statusFiltered = useMemo(() => {
        if (selectedStatus !== "") {
            return STATUS_OPTIONS;
        }

        const statusAux = [];
        selectedCompanyIndex.forEach((ind) => {
            const company = searchSupplier[ind];
            if (!company || !company.operations) return;

            company.operations.forEach((operation) => {
                if (!operation.modules || operation.modules.length === 0) return;

                let modulesAux = operation.modules;
                if (selectedModule !== "") {
                    modulesAux = modulesAux.filter(x => x.moduleID === parseInt(selectedModule));
                }

                modulesAux.forEach((module) => {
                    const statusExists = statusAux.find(x => x.text === module.status);
                    if (!statusExists) {
                        statusAux.push({
                            value: module.statusNumber,
                            text: module.status
                        });
                    }
                });
            });
        });

        return statusAux.sort((a, b) => a.text > b.text ? 1 : -1);
    }, [searchSupplier, selectedCompanyIndex, selectedModule, selectedStatus]);

    // Verificar se deve mostrar filtros de operação e módulo
    const showOperationModuleFilters = useMemo(() => {
        return !(user && user.roles && user.roles[0].id === ROLE_VIEWER && user.companyID == null);
    }, [user]);

    // Verificar se deve mostrar cards de status
    const shouldShowStatusCards = useMemo(() => {
        if (!user || !user.roles || !user.roles[0]) return false;
        const roleId = user.roles[0].id;
        return (
            roleId === ROLE_GLOBAL_APPROVER ||
            (roleId === ROLE_LOCAL_APPROVER && user.companyID == null) ||
            (roleId === ROLE_SUPPLIER && user.companyID == null) ||
            (roleId === ROLE_VIEWER && user.companyID == null)
        );
    }, [user]);

    // Load initial data
    useEffect(() => {
        const userObjectAux = JSON.parse(localStorage.getItem('@Thrive:user'));
        setUserObject(userObjectAux);
        setSelectedCulture(userObjectAux.selectedCultureID);
        loadCrops(userObjectAux);
        loadCompanies();
    }, []);

    // Load companies
    const loadCompanies = useCallback(async () => {
        if (companiesLoadedRef.current) return;
        
        try {
            const endpoint = user.groupID != null 
                ? 'Dashboard/getAllCompaniesFilteredByGroup'
                : `Dashboard/getAllCompanies?cultureID=${user.selectedCultureID}`;
            
            const result = await Api.get(endpoint);
            
            if (result && result.data) {
                setCompanies(result.data.response);
                setCompaniesDefault(result.data.response);
                setSearchSupplier(result.data.response);
                companiesLoadedRef.current = true;
            }
        } catch (error) {
            exceptionNotificationAPI(error);
        }
    }, [user.groupID, user.selectedCultureID]);

    // Load crops
    const loadCrops = useCallback(async (userObjectAux) => {
        try {
            const endpoint = `Dashboard/getAllCropsYears?selectedCulture=${userObjectAux.selectedCultureID ?? null}`;
            const result = await Api.get(endpoint);
            
            if (result && result.data) {
                setCrops(result.data.response);
                
                if (userObjectAux != null && userObjectAux.selectedReportYearID == null) {
                    const crop = result.data.response.find(x => x.reportYear === String(new Date().getFullYear()));
                    const targetCrop = crop || result.data.response[0];
                    
                    setSelectedCrop(targetCrop.reportYearID);
                    loadOperations(targetCrop.reportYearID);
                    loadModules(targetCrop.reportYearID);
                    
                    userObjectAux.selectedReportYearID = parseInt(targetCrop.reportYearID);
                    userObjectAux.selectedReportYear = targetCrop.year || targetCrop.reportYear;
                    localStorage.setItem('@Thrive:user', JSON.stringify(userObjectAux));
                    setUserObject(userObjectAux);
                } else if (userObjectAux != null && userObjectAux.selectedReportYearID) {
                    setSelectedCrop(userObjectAux.selectedReportYearID);
                    loadOperations(userObjectAux.selectedReportYearID);
                    loadModules(userObjectAux.selectedReportYearID);
                }
            }
        } catch (error) {
            exceptionNotificationAPI(error);
        }
    }, []);

    // Load operations
    const loadOperations = useCallback(async (reportYearID) => {
        try {
            const endpoint = `Dashboard/getAllOperationsByYear?reportYearID=${reportYearID}`;
            const result = await Api.get(endpoint);
            
            if (result && result.data) {
                setOperations(result.data.response);
            }
        } catch (error) {
            exceptionNotificationAPI(error);
        }
    }, []);

    // Load modules
    const loadModules = useCallback(async (reportYearID) => {
        try {
            const endpoint = `Dashboard/GetAllByReportYear?reportYearID=${reportYearID}`;
            const result = await Api.get(endpoint);
            
            if (result && result.data) {
                setModules(result.data.response);
            }
        } catch (error) {
            exceptionNotificationAPI(error);
        }
    }, []);

    // Fetch filtered data for companies
    const fetchCompanyData = useCallback(async (companyID) => {
        const endpoint = `Dashboard/GetByFilter?selectedCompanyID=${companyID}` +
            `&selectedOperation=${selectedOperation}&selectedReportYear=${selectedCrop}` +
            `&selectedModule=${selectedModule}&selectedStatus=${selectedStatus}` +
            `&selectedVersion=${selectedVersion}&selectedCulture=${selectedCulture}`;
        
        return Api.get(endpoint);
    }, [selectedOperation, selectedCrop, selectedModule, selectedStatus, selectedVersion, selectedCulture]);

    // Effect to fetch data when filters change
    useEffect(() => {
        if (selectedCompanyIndex.length === 0) {
            setStatusCard(false);
            return;
        }

        // Criar chave única para este fetch
        const fetchKey = `${selectedCompanyIndex.join(',')}-${selectedOperation}-${selectedModule}-${selectedStatus}-${selectedVersion}-${collapseAll}`;
        
        // Se já estamos fazendo fetch ou se já fizemos este fetch, não fazer novamente
        if (isFetchingRef.current || lastFetchKey.current === fetchKey) {
            return;
        }

        const fetchData = async () => {
            isFetchingRef.current = true;
            lastFetchKey.current = fetchKey;
            setStatusCard(true);
            setLoadingFilteredData(true);

            try {
                const targetIndexes = collapseAll 
                    ? searchSupplier.map((_, idx) => idx)
                    : selectedCompanyIndex;

                const promises = targetIndexes.map(async (idx) => {
                    const targetCompany = searchSupplier[idx];
                    if (!targetCompany) return null;
                    
                    const shouldFetch = 
                        !targetCompany.operations ||
                        targetCompany.operations.length === 0 ||
                        selectedOperation !== "" ||
                        selectedModule !== "" ||
                        selectedStatus !== "" ||
                        selectedVersion !== "";

                    if (shouldFetch) {
                        try {
                            const result = await fetchCompanyData(targetCompany.companyID);
                            if (result && result.data) {
                                return { ...targetCompany, operations: result.data.response };
                            }
                        } catch (err) {
                            console.error('Error fetching company data:', err);
                            return targetCompany;
                        }
                    }
                    return targetCompany;
                });

                const updatedCompanies = await Promise.all(promises);
                const validCompanies = updatedCompanies.filter(c => c !== null);
                
                // Atualizar searchSupplier ao invés de companies diretamente
                setSearchSupplier(prevSuppliers => {
                    const newSuppliers = [...prevSuppliers];
                    validCompanies.forEach((validCompany, idx) => {
                        const targetIdx = targetIndexes[idx];
                        if (targetIdx !== undefined && newSuppliers[targetIdx]) {
                            newSuppliers[targetIdx] = validCompany;
                        }
                    });
                    return newSuppliers;
                });
            } catch (error) {
                exceptionNotificationAPI(error);
            } finally {
                setLoadingFilteredData(false);
                setStatusCard(false);
                isFetchingRef.current = false;
            }
        };

        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompanyIndex, selectedOperation, selectedModule, selectedStatus, selectedVersion, collapseAll]);

    // Handle crop year change — same behavior as Authorized: update localStorage and reload page
    const handleCropChange = useCallback((cropValue) => {
        const cropId = cropValue === '' || cropValue == null ? null : parseInt(cropValue, 10);
        if (userObject != null && cropId != null) {
            const userStoraged = JSON.parse(localStorage.getItem('@Thrive:user'));
            if (userStoraged && userStoraged.selectedReportYearID !== cropId) {
                const targetCrop = crops.find(x => x.reportYearID === cropId);
                userStoraged.selectedReportYear = targetCrop ? targetCrop.year : '';
                userStoraged.selectedReportYearID = cropId;
                localStorage.setItem('@Thrive:user', JSON.stringify(userStoraged));
                window.location.reload(false);
                return;
            }
        }
        lastFetchKey.current = '';
        setSelectedCrop(cropValue);
        setSelectedOperation('');
        setSelectedModule('');
        setSelectedStatus('');
        loadOperations(cropValue);
        loadModules(cropValue);
    }, [userObject, crops, loadOperations, loadModules]);

    // Memoized callbacks
    const handleSearchChange = useCallback((searchValue) => {
        if (searchValue.length === 0) {
            setSearchSupplier(companiesDefault);
        } else if (searchValue.length >= 3) {
            const filteredSuppliers = companiesDefault.filter((supplier) =>
                supplier.name.toLowerCase().includes(searchValue.toLowerCase())
            );
            setSearchSupplier(filteredSuppliers);
        }
        handleCollapseAll();
    }, [companiesDefault]);

    const handleOperationChange = useCallback((value) => {
        lastFetchKey.current = ''; // Reset fetch key
        setSelectedOperation(value);
        setSelectedModule("");
        setSelectedStatus("");
    }, []);

    const handleModuleChange = useCallback((value) => {
        lastFetchKey.current = ''; // Reset fetch key
        setSelectedModule(value);
        setSelectedStatus("");
    }, []);

    const handleStatusChange = useCallback((value) => {
        lastFetchKey.current = ''; // Reset fetch key
        setSelectedStatus(value);
    }, []);

    const handleVersionChange = useCallback((value) => {
        lastFetchKey.current = ''; // Reset fetch key
        setSelectedVersion(value);
    }, []);

    const handleCollapseAll = useCallback(() => {
        lastFetchKey.current = ''; // Reset fetch key
        setCollapseAll(false);
        setSelectedCompanyIndex([]);
    }, []);

    const handleExpandAll = useCallback(() => {
        lastFetchKey.current = ''; // Reset fetch key
        setCollapseAll(true);
        const selected = searchSupplier.map((_, i) => i);
        setSelectedCompanyIndex(selected);
    }, [searchSupplier]);

    const handleToggleCompany = useCallback((index) => {
        lastFetchKey.current = ''; // Reset fetch key
        setSelectedCompanyIndex(prev => 
            prev.includes(index) 
                ? prev.filter(x => x !== index)
                : [...prev, index]
        );
    }, []);

    const handleModuleClick = useCallback((operationName, module, companyID, operationID) => {
        const userObjectToUpdate = { ...userObject, selectedCompanyID: companyID };
        localStorage.setItem('@Thrive:user', JSON.stringify(userObjectToUpdate));

        let url;
        if (module.cultureTypeID != null) {
            url = `/report/${operationName.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}/${module.description.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}/${module.cultureTypeID}`;
        } else {
            url = `/report/${operationName.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}/${module.description.replaceAll(' ', '').replaceAll('&', '').toLowerCase()}`;
        }

        navigate(url, { operationID });
    }, [userObject, navigate]);

    const handleCompareVersion = useCallback((companyID, reportYear, operationID, moduleID) => {
        navigate(`/compareVersion/${companyID}/${reportYear}/${operationID}/${moduleID}`);
    }, [navigate]);

    const handleDiscussionClick = useCallback((companyID, reportYear, operationID, moduleID) => {
        navigate(`/discussionDashboard/${companyID}/${reportYear}/${operationID}/${moduleID}`);
    }, [navigate]);

    const handleRedirectBI = useCallback(() => {
        window.open("https://app.powerbi.com/groups/me/apps/257047f9-5a20-4cc4-8fc8-46f7ba84d10c/reports/d5f328ee-17cb-44ba-805f-91ff77e4349b/ReportSection650d75efb75b006da952?ctid=ff9c7474-421d-4957-8d47-c4b64dec87b5&experience=power-bi", "_blank");
    }, []);

    return (
        <>
            <SessionTimeout />
            {companies ? (
                    <Container>
                    {/*{shouldShowStatusCards && (
                        <>
                            <StatusCards
                                notStarted={statusPercentages.notStarted}
                                partial={statusPercentages.partial}
                                final={statusPercentages.final}
                                completed={statusPercentages.completed}
                                loading={statusCard}
                                hasSelectedCompanies={selectedCompanyIndex.length > 0}
                            />
                                    <hr />
                                </>
                    )}*/}
                    
                    <FilterBar
                        crops={crops}
                        selectedCrop={selectedCrop}
                        operations={operations}
                        selectedOperation={selectedOperation}
                        modulesFiltered={modulesFiltered}
                        selectedModule={selectedModule}
                        statusFiltered={statusFiltered}
                        selectedStatus={selectedStatus}
                        version={VERSION_OPTIONS}
                        selectedVersion={selectedVersion}
                        userObject={userObject}
                        showOperationModuleFilters={showOperationModuleFilters}
                        onCropChange={handleCropChange}
                        onSearchChange={handleSearchChange}
                        onOperationChange={handleOperationChange}
                        onModuleChange={handleModuleChange}
                        onStatusChange={handleStatusChange}
                        onVersionChange={handleVersionChange}
                        onCollapseAll={handleCollapseAll}
                        onExpandAll={handleExpandAll}
                        onRedirectBI={handleRedirectBI}
                    />
                    
                    <div className="container">
                        {searchSupplier ? (
                            <Accordion className='dashboard'>
                                {searchSupplier.map((company, index) => (
                                    <CompanyAccordion
                                        key={company.companyID}
                                        company={company}
                                        index={index}
                                        isExpanded={selectedCompanyIndex.includes(index)}
                                        loadingFilteredData={loadingFilteredData}
                                        onToggle={handleToggleCompany}
                                        onModuleClick={handleModuleClick}
                                        onCompareVersion={handleCompareVersion}
                                        onDiscussionClick={handleDiscussionClick}
                                    />
                                ))}
                                    </Accordion>
                        ) : (
                                    <TableLoading />
                        )}
                        </div>
                </Container>
            ) : (
                    <TableLoading />
            )}
        </>
    );
}

export default DashboardGlobal;
